import { useState } from 'react';
import { IconButton } from '@/components/ui/button';
import { continuePausedSegmentationRun, useDevRuntimeStore } from '@modules/dev/devRuntime';
import useSegmentationStore from '@segmentation/hooks/useSegmentationStore';
import {
  SegmentationDebugStep,
  segmentationDebugStepLabels,
} from '@segmentation/dev/segmentationDebug';
import {
  downloadJson,
  loadDevSnapshotFromLocalStorage,
  pickDevSnapshotFile,
  saveDevSnapshotToLocalStorage,
} from '@segmentation/dev/segmentationDevSnapshot';
import JawSegmentation from '@segmentation/three/JawSegmentation';

const stepOptions = Object.values(SegmentationDebugStep);

export function SegmentationDevPanel() {
  const segmentation = useDevRuntimeStore((state) => state.segmentation);
  const pausedStep = useDevRuntimeStore((state) => state.pausedSegmentationStep);
  const setSegmentation = useDevRuntimeStore((state) => state.setSegmentation);
  const setSceneLayer = useDevRuntimeStore((state) => state.setSegmentationSceneLayer);

  const { scanId, jaws, setDevReadyJaw, update } = useSegmentationStore();
  const [devJaw, setDevJaw] = useState<'lower' | 'upper'>('lower');
  const [status, setStatus] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const getJawMesh = (): JawSegmentation | null => {
    if (!jaws) return null;
    return devJaw === 'lower' ? jaws.lowerJaw : jaws.upperJaw;
  };

  const handleSaveFile = () => {
    const mesh = getJawMesh();
    if (!mesh || mesh.toothBoundaryCurves.length === 0) {
      setStatus('Nothing to save — place anchors and edit contours first.');
      return;
    }
    const snapshot = mesh.exportDevSnapshot(scanId, devJaw);
    downloadJson(`segmentation-${scanId ?? 'scan'}-${devJaw}.json`, snapshot);
    setStatus(`Saved ${snapshot.teeth.length} tooth contour(s) to file.`);
  };

  const handleSaveLocal = () => {
    const mesh = getJawMesh();
    if (!mesh || mesh.toothBoundaryCurves.length === 0) {
      setStatus('Nothing to save — place anchors and edit contours first.');
      return;
    }
    const snapshot = mesh.exportDevSnapshot(scanId, devJaw);
    saveDevSnapshotToLocalStorage(snapshot);
    setStatus(`Saved ${snapshot.teeth.length} tooth contour(s) to localStorage.`);
  };

  const applySnapshot = (snapshot: ReturnType<JawSegmentation['exportDevSnapshot']> | null) => {
    if (!snapshot) {
      setStatus('Could not read snapshot file.');
      return;
    }
    if (snapshot.scanId && scanId && snapshot.scanId !== scanId) {
      setStatus(`Snapshot scanId mismatch (file=${snapshot.scanId}, current=${scanId}).`);
      return;
    }
    const mesh = snapshot.jaw === 'lower' ? jaws?.lowerJaw : jaws?.upperJaw;
    if (!mesh) {
      setStatus('Jaws not loaded yet.');
      return;
    }
    try {
      mesh.importDevSnapshot(snapshot);
      setDevJaw(snapshot.jaw);
      setDevReadyJaw(snapshot.jaw);
      update();
      setStatus(`Loaded ${snapshot.teeth.length} tooth contour(s) for ${snapshot.jaw} jaw.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Failed to import snapshot.');
    }
  };

  const handleLoadFile = async () => {
    const snapshot = await pickDevSnapshotFile();
    applySnapshot(snapshot);
  };

  const handleLoadLocal = () => {
    const snapshot = loadDevSnapshotFromLocalStorage(scanId, devJaw);
    if (!snapshot) {
      setStatus(`No localStorage snapshot for ${devJaw} jaw.`);
      return;
    }
    applySnapshot(snapshot);
  };

  const handleRunSegmentJaw = async () => {
    const mesh = getJawMesh();
    if (!mesh || mesh.toothBoundaryCurves.length === 0) {
      setStatus('No contours — save/load or create tooth boundaries first.');
      return;
    }
    setIsRunning(true);
    setStatus(`Running segmentJaw on ${devJaw} jaw…`);
    try {
      await jaws!.cutTeeth(devJaw);
      setStatus(`segmentJaw finished for ${devJaw} jaw. Check scene + Dev logs.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'segmentJaw failed.');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 text-xs text-white">
      <section className="rounded-2xl border border-white/10 bg-black/30 p-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/50">
          Contour snapshot
        </div>
        <p className="mb-3 text-white/60">
          Save edited tooth boundary curves after refine. Reload to skip anchor clicking and re-drawing.
        </p>

        <label className="mb-3 flex flex-col gap-1">
          <span className="text-white/70">Jaw</span>
          <select
            value={devJaw}
            onChange={(event) => setDevJaw(event.target.value as 'lower' | 'upper')}
            className="rounded-xl border border-white/10 bg-content-dark px-3 py-2 text-white"
          >
            <option value="lower">Lower</option>
            <option value="upper">Upper</option>
          </select>
        </label>

        <div className="grid grid-cols-2 gap-2">
          <IconButton variant="ghost-light" size="sm" onClick={handleSaveFile}>
            Save JSON
          </IconButton>
          <IconButton variant="ghost-light" size="sm" onClick={handleSaveLocal}>
            Save local
          </IconButton>
          <IconButton variant="ghost-light" size="sm" onClick={handleLoadFile}>
            Load JSON
          </IconButton>
          <IconButton variant="ghost-light" size="sm" onClick={handleLoadLocal}>
            Load local
          </IconButton>
        </div>

        <IconButton
          variant="ghost-success"
          size="sm"
          className="mt-3 w-full"
          disabled={isRunning}
          onClick={handleRunSegmentJaw}
        >
          {isRunning ? 'Running…' : 'Run segmentJaw'}
        </IconButton>

        {status && (
          <p className="mt-3 rounded-xl border border-cyan-200/10 bg-cyan-400/10 px-3 py-2 text-white/70">
            {status}
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-white/10 bg-black/30 p-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/50">Step Control</div>
        <p className="mb-3 rounded-xl border border-cyan-200/10 bg-cyan-400/10 px-3 py-2 text-white/60">
          Use the normal segmentation UI to select teeth and run the jaw. These controls only change where the pipeline stops and what it records.
        </p>
        {pausedStep && (
          <div className="mb-3 rounded-xl border border-amber-300/30 bg-amber-400/10 p-3">
            <div className="mb-2 text-white/80">
              Paused after <span className="font-semibold text-amber-100">{segmentationDebugStepLabels[pausedStep]}</span>
            </div>
            <IconButton
              onClick={continuePausedSegmentationRun}
              variant="ghost-success"
              size="sm"
              className="w-full"
            >
              Continue to next step
            </IconButton>
          </div>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-white/70">Stop after</span>
          <select
            value={segmentation.stopAfter ?? ''}
            onChange={(event) =>
              setSegmentation({
                stopAfter: event.target.value ? (event.target.value as SegmentationDebugStep) : null,
              })
            }
            className="rounded-xl border border-white/10 bg-content-dark px-3 py-2 text-white"
          >
            <option value="">Run full pipeline</option>
            {stepOptions.map((step) => (
              <option key={step} value={step}>
                {segmentationDebugStepLabels[step]}
              </option>
            ))}
          </select>
        </label>

        <label className="mt-3 flex flex-col gap-1">
          <span className="text-white/70">Log level</span>
          <select
            value={segmentation.logLevel}
            onChange={(event) => setSegmentation({ logLevel: event.target.value as typeof segmentation.logLevel })}
            className="rounded-xl border border-white/10 bg-content-dark px-3 py-2 text-white"
          >
            <option value="off">Off</option>
            <option value="warn">Warnings</option>
            <option value="debug">Debug</option>
          </select>
        </label>
      </section>

      <section className="rounded-2xl border border-white/10 bg-black/30 p-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/50">Scene Layers</div>
        {(Object.keys(segmentation.sceneLayers) as Array<keyof typeof segmentation.sceneLayers>).map((key) => (
          <label key={key} className="flex items-center justify-between gap-3 py-1">
            <span className="capitalize text-white/70">{key.replace(/([A-Z])/g, ' $1')}</span>
            <input
              type="checkbox"
              checked={segmentation.sceneLayers[key]}
              onChange={(event) => {
                const checked = event.target.checked;
                setSceneLayer(key, checked);
                if (key === 'toothBorders' && checked) {
                  getJawMesh()?.refreshToothBordersDebugLayer();
                }
              }}
            />
          </label>
        ))}
      </section>
    </div>
  );
}
